/**
 * Hooks TanStack Query pour les thèmes.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Theme } from '@jeraisonne/db';
import type { CreateThemeInput, ModerateThemeInput } from '@jeraisonne/db/schemas/themes';
import {
  createTheme,
  getThemeBySlug,
  listAccessibleThemes,
  listAuthoredThemes,
  listPendingReviewThemes,
  listThemeExercises,
  moderateTheme,
  submitThemeForReview,
  withdrawThemeToPrivate,
} from './themes-api';

const THEME_KEYS = {
  all: ['themes'] as const,
  accessible: (filters?: Record<string, unknown>) =>
    ['themes', 'accessible', filters ?? {}] as const,
  bySlug: (slug: string) => ['themes', 'bySlug', slug] as const,
  authored: (authorId: string) => ['themes', 'authored', authorId] as const,
  pendingReview: () => ['themes', 'pending'] as const,
  exercises: (themeId: string) => ['themes', 'exercises', themeId] as const,
};

export function useAccessibleThemes(filters?: { minLevel?: string | null; search?: string }) {
  return useQuery({
    queryKey: THEME_KEYS.accessible(filters),
    queryFn: () => listAccessibleThemes(filters),
    staleTime: 60_000,
  });
}

export function useThemeBySlug(slug: string | undefined) {
  return useQuery({
    queryKey: THEME_KEYS.bySlug(slug ?? ''),
    queryFn: () => getThemeBySlug(slug!),
    enabled: !!slug,
    staleTime: 30_000,
  });
}

export function useThemeExercises(themeId: string | undefined) {
  return useQuery({
    queryKey: THEME_KEYS.exercises(themeId ?? ''),
    queryFn: () => listThemeExercises(themeId!),
    enabled: !!themeId,
    staleTime: 30_000,
  });
}

export function useAuthoredThemes(authorId: string | undefined) {
  return useQuery({
    queryKey: THEME_KEYS.authored(authorId ?? ''),
    queryFn: () => listAuthoredThemes(authorId!),
    enabled: !!authorId,
    staleTime: 30_000,
  });
}

export function usePendingReviewThemes(enabled: boolean) {
  return useQuery({
    queryKey: THEME_KEYS.pendingReview(),
    queryFn: () => listPendingReviewThemes(),
    enabled,
    staleTime: 30_000,
  });
}

export function useCreateTheme() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateThemeInput) => createTheme(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: THEME_KEYS.all }),
  });
}

export function useSubmitForReview() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (themeId: string) => submitThemeForReview(themeId),
    onSuccess: () => qc.invalidateQueries({ queryKey: THEME_KEYS.all }),
  });
}

export function useWithdrawToPrivate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (themeId: string) => withdrawThemeToPrivate(themeId),
    onSuccess: () => qc.invalidateQueries({ queryKey: THEME_KEYS.all }),
  });
}

export function useModerateTheme() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ModerateThemeInput) => moderateTheme(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: THEME_KEYS.all }),
  });
}

export type { Theme };
